import React from "react";
import { Navigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Server-side RBAC is authoritative; this only stops forbidden pages from rendering.
export const ROUTE_ROLES = {
  "": ["owner", "admin", "finance", "partner", "driver"],
  partner: ["owner", "partner"],
  "partner-commissions": ["owner", "finance", "partner"],
  payouts: ["owner", "finance", "partner"],
  "reports/hub-performance": ["owner", "finance"],
  "reports/deposits": ["owner", "finance"],
  receipts: ["owner", "admin"],
  bookings: ["owner", "admin", "finance"],
  "bookings/new": ["owner", "admin"],
  calendar: ["owner", "admin", "driver"],
  capture: ["owner"],
  reminders: ["owner", "admin"],
  whatsapp: ["owner", "admin"],
  ai: ["owner"],
  customers: ["owner"],
  units: ["owner"],
  branches: ["owner"],
  packages: ["owner"],
  promotions: ["owner"],
  "delivery-areas": ["owner"],
  addons: ["owner"],
  payments: ["owner", "finance"],
  "payment-proofs": ["owner", "finance"],
  returns: ["owner", "finance"],
  scan: ["owner", "admin", "driver"],
  labels: ["owner", "admin", "driver"],
  "online-payments": ["owner", "finance"],
  "sandbox-pay": ["owner"],
  "reports/margin": ["owner", "finance"],
  "reports/sources": ["owner", "finance"],
  "site-media": ["owner"],
  inspections: ["owner"],
  users: ["owner"],
  "audit-logs": ["owner"],
  summary: ["owner", "finance"],
  "weekly-summary": ["owner", "finance"],
  "go-live": ["owner"],
  settings: ["owner"],
};

// A page also opens up when the owner granted the matching extra permission (users.perms[]).
export const ROUTE_PERMS = {
  "": "reports:read",
  partner: "partner:venue",
  "partner-commissions": "reports:read",
  payouts: "reports:read",
  "reports/hub-performance": "reports:read",
  "reports/deposits": "reports:read",
  bookings: "bookings:read",
  "bookings/new": "bookings:write",
  calendar: "bookings:read",
  capture: "media:write",
  reminders: "bookings:write",
  whatsapp: "bookings:write",
  ai: "reports:read",
  customers: "customers:read",
  units: "units:write",
  branches: "units:write",
  packages: "settings:write",
  promotions: "settings:write",
  "delivery-areas": "settings:write",
  addons: "settings:write",
  payments: "payments:write",
  "payment-proofs": "payments:write",
  "online-payments": "payments:write",
  "sandbox-pay": "payments:write",
  "reports/margin": "reports:read",
  "reports/sources": "reports:read",
  "site-media": "settings:write",
  inspections: "inspections:write",
  returns: ["payments:write", "inspections:write"],
  "audit-logs": "reports:read",
  summary: "reports:read",
  "weekly-summary": "reports:read",
  "go-live": "reports:read",
  settings: "settings:write",
};

export const canSee = (user, area) => {
  if (!user) return false;
  const ownerOnly = ["capture", "customers", "units", "branches", "inspections"];
  if (ownerOnly.includes(area) && user.role !== "owner") return false;
  const driverHidden = ["capture", "whatsapp", "customers", "units", "branches", "summary", "ai"];
  if (user.role === "driver" && driverHidden.includes(area)) return false;
  if ((ROUTE_ROLES[area] || ["owner"]).includes(user.role)) return true;
  const perms = Array.isArray(ROUTE_PERMS[area])
    ? ROUTE_PERMS[area]
    : [ROUTE_PERMS[area]];
  return perms.some((perm) => perm && (user.perms || []).includes(perm));
};

const HOME_FOR = { driver: "/admin", finance: "/admin", partner: "/admin" };

export const RoleGuard = ({ area, children }) => {
  const { user } = useAuth();
  if (!user) return null;
  if (canSee(user, area)) return children;
  const home = HOME_FOR[user.role];
  if (home) return <Navigate to={home} replace />;
  return (
    <div className="max-w-md" data-testid="forbidden">
      <ShieldAlert className="text-lm-red" size={32} />
      <h1 className="mt-3 font-display text-2xl tracking-tight">Not available for your role</h1>
      <p className="mt-2 text-sm text-ink-soft">
        Your account role is <span className="text-ink">{user.role}</span>, which does not have access to this page.
        Ask the owner to change your role in Staff Accounts.
      </p>
    </div>
  );
};
